import { useEffect, type ReactNode } from "react";

import { t } from "@/i18n";

/**
 * Tasarim sistemi ilkel bilesenleri — eski arayuzun gorsel dili
 * (docs/arayuz-tasarim-rehberi.md). Sinif adlari src/index.css `@layer
 * components` icinde tanimlidir; sayfalar ham Tailwind yerine bunlari kullanir.
 */

// ---------------------------------------------------------------------------
// Bolum basligi
// ---------------------------------------------------------------------------

interface SectionHeaderProps {
  title: string;
  note?: ReactNode;
  /** Sagda hizalanan kontroller (sekme, secici, dugme). */
  actions?: ReactNode;
  id?: string;
  className?: string;
}

/** `.sec` — mavi cubuk + kucuk buyuk harf baslik + isteğe bagli not/kontrol. */
export function SectionHeader({ title, note, actions, id, className = "" }: SectionHeaderProps) {
  return (
    <div className={`flex flex-wrap items-end justify-between gap-2 ${className}`}>
      <div className="min-w-0 flex-1"><h2 id={id} className="sec !my-0">{title}</h2>{note && <p className="sec-note mt-1">{note}</p>}</div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Hap rozet
// ---------------------------------------------------------------------------

export type PillTone = "ok" | "warn" | "bad" | "info" | "muted";

export function Pill({ tone = "muted", children, className = "", title }: { tone?: PillTone; children: ReactNode; className?: string; title?: string }) {
  return (
    <span className={`pill pill-${tone} ${className}`} title={title}>
      {children}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Buton
// ---------------------------------------------------------------------------

type ButtonVariant = "primary" | "outline" | "ghost" | "danger" | "link";

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: "sm" | "md";
}

export function Button({ variant = "outline", size = "md", className = "", type = "button", ...rest }: ButtonProps) {
  const cls = variant === "link" ? "btn-link" : `btn btn-${variant} ${size === "sm" ? "btn-sm" : ""}`;
  return <button type={type} className={`${cls} ${className}`} {...rest} />;
}

// ---------------------------------------------------------------------------
// Bolumlu sekme (segmented control) — donem, filtre, gorunum secimleri
// ---------------------------------------------------------------------------

export interface SegmentedOption<V extends string | number> {
  value: V;
  label: ReactNode;
  title?: string;
}

interface SegmentedTabsProps<V extends string | number> {
  value: V;
  options: SegmentedOption<V>[];
  onChange: (value: V) => void;
  ariaLabel: string;
  size?: "sm" | "md";
  className?: string;
}

export function SegmentedTabs<V extends string | number>({ value, options, onChange, ariaLabel, size = "md", className = "" }: SegmentedTabsProps<V>) {
  const h = size === "sm" ? "min-h-[32px] px-2.5 text-xs" : "min-h-[38px] px-3 text-[13px]";
  return (
    <div role="tablist" aria-label={ariaLabel} className={`inline-flex max-w-full gap-0.5 overflow-x-auto rounded-[9px] bg-bg p-0.5 ${className}`}>
      {options.map((opt) => {
        const selected = opt.value === value;
        return (
          <button
            key={String(opt.value)}
            type="button"
            role="tab"
            aria-selected={selected}
            title={opt.title}
            onClick={() => onChange(opt.value)}
            className={`${h} whitespace-nowrap rounded-[7px] font-semibold transition-colors ${
              selected ? "bg-card text-brand-primary shadow-sm" : "text-muted hover:text-ink"
            }`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Modal kabugu — tum modallar bunu kullanir (baslik + sag ustte X + govde)
// ---------------------------------------------------------------------------

interface ModalShellProps {
  open: boolean;
  title: string;
  onClose: () => void;
  /** Baslik satirinda X'in solunda duran kontroller (ör. AI analiz dugmesi). */
  actions?: ReactNode;
  /** Baslik ile govde arasinda tam genislik bir serit (sekmeler). */
  tabs?: ReactNode;
  size?: "sm" | "md" | "lg" | "xl";
  children: ReactNode;
  testId?: string;
}

const MODAL_W: Record<NonNullable<ModalShellProps["size"]>, string> = {
  sm: "max-w-md",
  md: "max-w-2xl",
  lg: "max-w-4xl",
  xl: "max-w-6xl",
};

export function ModalShell({ open, title, onClose, actions, tabs, size = "lg", children, testId }: ModalShellProps) {
  useEffect(() => {
    if (!open) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKeyDown);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-navy/55 p-2 backdrop-blur-[2px] sm:items-center sm:p-4" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        data-testid={testId}
        onClick={(e) => e.stopPropagation()}
        className={`flex max-h-[96vh] w-full flex-col overflow-hidden rounded-2xl bg-card shadow-modal sm:max-h-[90vh] ${MODAL_W[size]}`}
      >
        <div className="flex items-center gap-3 border-b-[1.5px] border-line py-3 pl-5 pr-3">
          <h2 className="min-w-0 flex-1 truncate font-display text-[17px] font-bold text-ink">{title}</h2>
          {actions}
          <button
            type="button"
            onClick={onClose}
            aria-label={t("common.close")}
            className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-blue-pale text-lg leading-none text-muted transition-colors hover:bg-red-pale hover:text-red"
          >
            ✕
          </button>
        </div>
        {tabs && <div className="border-b-[1.5px] border-line bg-[#f8faff] px-4">{tabs}</div>}
        <div className="min-h-0 flex-1 overflow-auto px-4 py-4 sm:px-5">{children}</div>
      </div>
    </div>
  );
}

/** Modal ust seridi icin alt cizgili sekmeler (`tabs` prop'una verilir). */
export function ModalTabs<V extends string>({ value, options, onChange, ariaLabel }: { value: V; options: SegmentedOption<V>[]; onChange: (v: V) => void; ariaLabel: string }) {
  return (
    <div role="tablist" aria-label={ariaLabel} className="flex gap-1 overflow-x-auto overflow-y-hidden">
      {options.map((opt) => {
        const selected = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            role="tab"
            aria-selected={selected}
            onClick={() => onChange(opt.value)}
            className={`-mb-[1.5px] min-h-[42px] whitespace-nowrap border-b-[2.5px] px-3.5 text-sm font-semibold transition-colors ${
              selected ? "border-brand-primary text-brand-primary" : "border-transparent text-muted hover:text-ink"
            }`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Serit (alarm/bilgi)
// ---------------------------------------------------------------------------

export type RibbonTone = "ok" | "warn" | "bad" | "info";

const RIBBON_ICON: Record<RibbonTone, string> = { ok: "✓", warn: "!", bad: "!", info: "i" };
const RIBBON_ICON_BG: Record<RibbonTone, string> = { ok: "bg-green", warn: "bg-amber", bad: "bg-red", info: "bg-brand-primary" };

export function Ribbon({ tone, children, className = "" }: { tone: RibbonTone; children: ReactNode; className?: string }) {
  return (
    <div role="status" className={`ribbon ribbon-${tone} ${className}`}>
      <span aria-hidden="true" className={`grid h-6 w-6 shrink-0 place-items-center rounded-full text-xs font-bold text-white ${RIBBON_ICON_BG[tone]}`}>
        {RIBBON_ICON[tone]}
      </span>
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Marka
// ---------------------------------------------------------------------------

export function BrandMark({ size = 40, className = "" }: { size?: number; className?: string }) {
  return (
    <img src="/icons/asakai-logo.png?v=20261019" width={size} height={size} alt="AsakAI" className={`object-contain ${className}`} />
  );
}

/** "Asak" koyu + "AI" soluk marka yazisi. */
export function BrandName({ className = "" }: { className?: string }) {
  return (
    <span className={`font-display font-bold tracking-tight text-navy ${className}`} data-testid="brand">
      Asak<span className="font-medium text-muted">AI</span>
    </span>
  );
}
